import { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Sparkles } from "lucide-react";
import { useFieldSchema } from "@/hooks/use-import";
import type {
  CsvValidationResponse,
  CustomFieldDefinition,
  ImportEntityType,
} from "@adva/types";
import type { ColumnMappingOverride } from "../wizard/state";
import { InlineCreateField } from "./inline-create-field";

interface ColumnMapperProps {
  entityType: ImportEntityType;
  validation: CsvValidationResponse;
  overrides: Record<string, ColumnMappingOverride>;
  onSetMapping: (override: ColumnMappingOverride) => void;
  onClearMapping: (source_header: string) => void;
  onFieldCreated: (definition: CustomFieldDefinition) => void;
  onError: (message: string) => void;
}

interface RowInfo {
  source_header: string;
  bucket: "core" | "custom_mapped" | "ignored" | "unmapped";
  field_key: string | null;
  sampleValues: string[];
}

/**
 * Build bucketed row info by combining the server-side bucketing from
 * `column_mapping` with any client-side user overrides. An override wins
 * over the server bucket; unmapped rows carry sample values for context.
 */
function buildRows(
  validation: CsvValidationResponse,
  overrides: Record<string, ColumnMappingOverride>,
): RowInfo[] {
  const { column_mapping, preview } = validation;
  const rowsByHeader = new Map<string, RowInfo>();

  // Header order: pull from the first preview row when available so the
  // mapper matches the user's CSV column order. Fall back to bucketed
  // categories otherwise.
  const previewHeaders = Object.keys(preview[0] ?? {});
  const allHeaders: string[] = [];
  const seen = new Set<string>();
  const pushUnique = (h: string) => {
    if (!seen.has(h)) {
      seen.add(h);
      allHeaders.push(h);
    }
  };
  previewHeaders.forEach(pushUnique);
  column_mapping.matched.forEach(pushUnique);
  (column_mapping.custom_mapped ?? []).forEach((c) => pushUnique(c.header));
  (column_mapping.ignored ?? []).forEach(pushUnique);
  (column_mapping.unmapped ?? column_mapping.unmatched_headers ?? []).forEach(
    pushUnique,
  );

  const customMapByHeader = new Map<string, string>();
  (column_mapping.custom_mapped ?? []).forEach((c) =>
    customMapByHeader.set(c.header, c.field_key),
  );
  const matched = new Set(column_mapping.matched);
  const ignored = new Set(column_mapping.ignored ?? []);

  for (const header of allHeaders) {
    const sampleValues = preview
      .slice(0, 5)
      .map((row) => row[header])
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)));

    const override = overrides[header];
    if (override) {
      if (override.target_kind === "ignore") {
        rowsByHeader.set(header, {
          source_header: header,
          bucket: "ignored",
          field_key: null,
          sampleValues,
        });
        continue;
      }
      if (override.target_kind === "core") {
        rowsByHeader.set(header, {
          source_header: header,
          bucket: "core",
          field_key: override.target_field,
          sampleValues,
        });
        continue;
      }
      if (override.target_kind === "custom") {
        rowsByHeader.set(header, {
          source_header: header,
          bucket: "custom_mapped",
          field_key: override.target_field,
          sampleValues,
        });
        continue;
      }
    }

    if (matched.has(header)) {
      rowsByHeader.set(header, {
        source_header: header,
        bucket: "core",
        field_key: header,
        sampleValues,
      });
      continue;
    }
    const customKey = customMapByHeader.get(header);
    if (customKey) {
      rowsByHeader.set(header, {
        source_header: header,
        bucket: "custom_mapped",
        field_key: customKey,
        sampleValues,
      });
      continue;
    }
    if (ignored.has(header)) {
      rowsByHeader.set(header, {
        source_header: header,
        bucket: "ignored",
        field_key: null,
        sampleValues,
      });
      continue;
    }
    rowsByHeader.set(header, {
      source_header: header,
      bucket: "unmapped",
      field_key: null,
      sampleValues,
    });
  }

  return allHeaders.map((h) => rowsByHeader.get(h)!);
}

export function ColumnMapper(props: ColumnMapperProps) {
  const {
    entityType,
    validation,
    overrides,
    onSetMapping,
    onClearMapping,
    onFieldCreated,
    onError,
  } = props;
  const rows = useMemo(
    () => buildRows(validation, overrides),
    [validation, overrides],
  );
  const unmappedCount = rows.filter((r) => r.bucket === "unmapped").length;
  const ignoredCount = rows.filter((r) => r.bucket === "ignored").length;
  const customCount = rows.filter((r) => r.bucket === "custom_mapped").length;
  const coreCount = rows.filter((r) => r.bucket === "core").length;

  const schemaQuery = useFieldSchema(entityType);
  const customDefinitions = useMemo(
    () => Object.values<CustomFieldDefinition>(schemaQuery.data?.schema ?? {}),
    [schemaQuery.data],
  );

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        {unmappedCount === 0 ? (
          <CheckCircle2 className="h-4 w-4 text-primary" />
        ) : (
          <AlertCircle className="h-4 w-4 text-amber-600" />
        )}
        <h3 className="text-sm font-semibold">Column mapping</h3>
        <span className="text-xs text-muted-foreground ml-1">
          {coreCount} core · {customCount} custom · {ignoredCount} ignored ·{" "}
          {unmappedCount} unmapped
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No headers detected in the CSV.
        </div>
      ) : (
        <ul className="divide-y">
          {rows.map((row) => (
            <li key={row.source_header} className="py-2">
              <ColumnRow
                row={row}
                entityType={entityType}
                customDefinitions={customDefinitions}
                onSetMapping={onSetMapping}
                onClearMapping={onClearMapping}
                onFieldCreated={onFieldCreated}
                onError={onError}
              />
            </li>
          ))}
        </ul>
      )}

      {unmappedCount === 0 && rows.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-md border bg-muted/30 p-2 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>
            All CSV headers matched a known field. Your choices will be
            remembered for the next import.
          </span>
        </div>
      )}
    </div>
  );
}

function ColumnRow({
  row,
  entityType,
  customDefinitions,
  onSetMapping,
  onClearMapping,
  onFieldCreated,
  onError,
}: {
  row: RowInfo;
  entityType: ImportEntityType;
  customDefinitions: CustomFieldDefinition[];
  onSetMapping: (override: ColumnMappingOverride) => void;
  onClearMapping: (source_header: string) => void;
  onFieldCreated: (definition: CustomFieldDefinition) => void;
  onError: (message: string) => void;
}) {
  const [creating, setCreating] = useState(false);

  const bucketClasses: Record<RowInfo["bucket"], string> = {
    core: "bg-primary/10 text-primary border-primary/30",
    custom_mapped: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    ignored: "bg-muted text-muted-foreground border-border",
    unmapped: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  };
  const bucketLabel: Record<RowInfo["bucket"], string> = {
    core: "Core",
    custom_mapped: "Custom",
    ignored: "Ignored",
    unmapped: "Unmapped",
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
      <div className="md:col-span-3 flex items-start gap-2">
        <span
          className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${bucketClasses[row.bucket]}`}
        >
          {bucketLabel[row.bucket]}
        </span>
        <span className="font-mono text-xs break-all">{row.source_header}</span>
      </div>
      <div className="md:col-span-3 text-xs">
        {row.bucket === "core" && (
          <span className="text-muted-foreground">
            → core column <span className="font-mono">{row.field_key}</span>
          </span>
        )}
        {row.bucket === "custom_mapped" && (
          <span className="text-muted-foreground">
            → custom <span className="font-mono">{row.field_key}</span>
          </span>
        )}
        {row.bucket === "ignored" && (
          <span className="text-muted-foreground">Dropped on import</span>
        )}
        {row.bucket === "unmapped" && (
          <span className="text-amber-700">Pick a mapping →</span>
        )}
      </div>
      <div className="md:col-span-4 text-xs">
        {row.sampleValues.length > 0 ? (
          <div
            className="text-muted-foreground truncate"
            title={row.sampleValues.join(" · ")}
          >
            e.g. {row.sampleValues.slice(0, 3).join(" · ")}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
      <div className="md:col-span-2 flex flex-col items-stretch gap-1">
        {row.bucket === "core" ? (
          <span className="text-xs text-muted-foreground text-right">
            Read-only
          </span>
        ) : (
          <RowActions
            row={row}
            entityType={entityType}
            customDefinitions={customDefinitions}
            onSetMapping={onSetMapping}
            onClearMapping={onClearMapping}
            onCreateClick={() => setCreating(true)}
          />
        )}
      </div>

      {creating && (
        <div className="md:col-span-12 mt-2">
          <InlineCreateField
            entityType={entityType}
            sourceHeader={row.source_header}
            onCreated={(def) => {
              setCreating(false);
              onSetMapping({
                source_header: row.source_header,
                target_kind: "custom",
                target_field: def.field_key,
              });
              onFieldCreated(def);
            }}
            onCancel={() => setCreating(false)}
            onError={onError}
          />
        </div>
      )}
    </div>
  );
}

function RowActions({
  row,
  entityType: _entityType,
  customDefinitions,
  onSetMapping,
  onClearMapping,
  onCreateClick,
}: {
  row: RowInfo;
  entityType: ImportEntityType;
  customDefinitions: CustomFieldDefinition[];
  onSetMapping: (override: ColumnMappingOverride) => void;
  onClearMapping: (source_header: string) => void;
  onCreateClick: () => void;
}) {
  const handleSelectChange = (value: string) => {
    if (value === "__ignore__") {
      onSetMapping({
        source_header: row.source_header,
        target_kind: "ignore",
        target_field: null,
      });
      return;
    }
    if (value === "__create__") {
      onCreateClick();
      return;
    }
    if (value === "__clear__") {
      onClearMapping(row.source_header);
      return;
    }
    if (value === "") return;
    // value is a custom field_key
    onSetMapping({
      source_header: row.source_header,
      target_kind: "custom",
      target_field: value,
    });
  };

  const currentValue =
    row.bucket === "ignored"
      ? "__ignore__"
      : row.bucket === "custom_mapped" && row.field_key
        ? row.field_key
        : "";

  return (
    <div className="flex flex-col gap-1">
      <select
        value={currentValue}
        onChange={(e) => handleSelectChange(e.target.value)}
        className="w-full rounded-md border bg-background px-2 py-1 text-xs"
        aria-label={`Mapping for ${row.source_header}`}
      >
        <option value="">Pick action…</option>
        <option value="__ignore__">Ignore on import</option>
        {customDefinitions.length > 0 && (
          <optgroup label="Map to existing custom field">
            {customDefinitions.map((d) => (
              <option key={d.field_key} value={d.field_key}>
                {d.label} ({d.field_key})
              </option>
            ))}
          </optgroup>
        )}
        <option value="__create__">Create new custom field…</option>
        {row.bucket !== "unmapped" && (
          <option value="__clear__">Reset to auto-detected</option>
        )}
      </select>
    </div>
  );
}
